import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiRequestsService } from '../../services/api-requests.service';

type MessageType = 'CONFIRMATION' | 'INDIVIDUAL' | 'FLOW';

interface SalaryEntry {
  date: string;
  description: string;
  amount: string;
}

interface SalaryItem {
  id: string;
  text: string;
  title: string;
}

interface SalaryReply {
  inboundMsgId: string;
  kind: string;
  text: string;
  selectedItemIds?: string[];
  flowResponse?: any;
  receivedAt: string;
}

interface ConfirmedDate {
  entryIndex: number;
  entryDescription: string;
  confirmedDate: string;
  confirmedDateDisplay: string;
}

interface SalaryConfirmation {
  _id: string;
  messageType: MessageType;
  messageText: string;
  recipientNo: string;
  items: SalaryItem[];
  sendMode: 'SESSION' | 'TEMPLATE' | null;
  deliveryStatus: 'QUEUED' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  failedReason?: string;
  interpretation: string;
  lastReply?: SalaryReply;
  replies: SalaryReply[];
  selectedItemIds?: string[];
  confirmedDates?: ConfirmedDate[];
  createdAt: string;
  repliedAt?: string;
}

const API_BASE = 'api/v1/salary-confirmation';
const REFRESH_INTERVAL_MS = 15000;
const MAX_MESSAGE_LENGTH = 1024;
const MAX_ENTRIES = 20;

@Component({
  selector: 'app-salary-confirmation',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './salary-confirmation.component.html',
  styleUrl: './salary-confirmation.component.scss',
})
export class SalaryConfirmationComponent implements OnInit, OnDestroy {
  readonly maxMessageLength = MAX_MESSAGE_LENGTH;
  readonly maxEntries = MAX_ENTRIES;

  readonly messageTypes: { value: MessageType; label: string; hint: string }[] = [
    { value: 'CONFIRMATION', label: 'Confirmation', hint: 'Recipient replies with OK / Not OK.' },
    { value: 'INDIVIDUAL', label: 'Individual Confirmation', hint: 'Recipient ticks each entry that is correct.' },
    { value: 'FLOW', label: 'Flow', hint: 'Opens a WhatsApp form to confirm the transaction with dates.' },
  ];

  readonly interpretationLabels: { [key: string]: string } = {
    PENDING: 'Pending',
    CONFIRMED: 'Confirmed',
    NOT_CONFIRMED: 'Not Confirmed',
    PARTIAL: 'Partially Confirmed',
    FLOW_SUBMITTED: 'Form Submitted',
    TEXT_REPLY: 'Text Reply',
  };

  readonly deliveryStatuses = [
    { value: 'QUEUED', label: 'Queued' },
    { value: 'SENT', label: 'Sent' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'READ', label: 'Read' },
    { value: 'FAILED', label: 'Failed' },
  ];

  // ── Structured form fields ─────────────────────────────────────────────────
  messageType: MessageType = 'CONFIRMATION';
  employeeName = '';
  bankName = '';
  accountNo = '';
  salaryEntries: SalaryEntry[] = [
    { date: '', description: '', amount: '' },
  ];
  recipientNo = '';
  isSending = false;
  formError = '';
  formSuccess = '';
  formWarning = '';

  // Message type whose info popover is open
  infoType: MessageType | null = null;

  // Message type availability (Flow ids are configured in backend_wa)
  config = { individualEnabled: false, flowEnabled: false };

  // History
  rows: SalaryConfirmation[] = [];
  total = 0;
  isLoadingList = false;
  listError = '';
  search = '';
  deliveryFilter = '';
  expandedId: string | null = null;
  resendingId: string | null = null;
  lastRefreshedAt: Date | null = null;
  private refreshTimer: any = null;

  constructor(private apiService: ApiRequestsService) {}

  ngOnInit(): void {
    this.loadConfig();
    this.loadList();
    this.refreshTimer = setInterval(() => {
      if (!document.hidden) this.loadList(true);
    }, REFRESH_INTERVAL_MS);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  // ── Composed message (built from structured fields) ───────────────────────

  get composedMessage(): string {
    const lines: string[] = [];
    if (this.employeeName.trim()) lines.push(this.employeeName.trim());
    const bankLine = [this.bankName.trim(), this.accountNo.trim()].filter(Boolean).join('    ');
    if (bankLine) lines.push(bankLine);
    for (const e of this.salaryEntries) {
      const parts = [this.formatDate(e.date), e.description.trim(), e.amount.trim(), 'CR'].filter(Boolean);
      if (parts.length > 1) lines.push(parts.join(' '));
    }
    return lines.join('\n');
  }

  // Items for INDIVIDUAL type — derived automatically from filled entries
  get filledItems(): string[] {
    return this.salaryEntries
      .filter(e => e.date.trim() || e.description.trim() || e.amount.trim())
      .map(e => [this.formatDate(e.date), e.description.trim(), e.amount.trim(), 'CR'].filter(Boolean).join(' '));
  }

  // ── Form ──────────────────────────────────────────────────────────────────

  toggleInfo(type: MessageType, event: Event): void {
    event.stopPropagation();
    this.infoType = this.infoType === type ? null : type;
  }

  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  closeInfo(): void {
    this.infoType = null;
  }

  isTypeEnabled(type: MessageType): boolean {
    if (type === 'INDIVIDUAL') return this.config.individualEnabled;
    if (type === 'FLOW') return this.config.flowEnabled;
    return true;
  }

  onMessageTypeChange(): void {
    this.employeeName = '';
    this.bankName = '';
    this.accountNo = '';
    this.salaryEntries = [{ date: '', description: '', amount: '' }];
    this.recipientNo = '';
    this.clearFormMessages();
  }

  // yyyy-mm-dd → dd/mm/yyyy for the composed message text
  formatDate(isoDate: string): string {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${d}/${m}/${y}`;
  }

  // yyyy-mm-dd → dd/mm/yy for the date picker display label
  formatDateDisplay(isoDate: string): string {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${d}/${m}/${y.slice(2)}`;
  }

  addEntry(): void {
    if (this.salaryEntries.length >= MAX_ENTRIES) return;
    this.salaryEntries = [...this.salaryEntries, { date: '', description: '', amount: '' }];
  }

  removeEntry(index: number): void {
    if (this.salaryEntries.length === 1) return;
    this.salaryEntries = this.salaryEntries.filter((_, i) => i !== index);
  }

  trackByIndex(index: number): number {
    return index;
  }

  get canSend(): boolean {
    if (this.isSending || !this.recipientNo.trim()) return false;
    const msg = this.composedMessage;
    if (!msg.trim() || msg.length > MAX_MESSAGE_LENGTH) return false;
    if (this.messageType === 'INDIVIDUAL' && !this.filledItems.length) return false;
    return true;
  }

  send(): void {
    this.clearFormMessages();
    const digits = this.recipientNo.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) {
      this.formError = 'Please enter a valid WhatsApp number (10 digits, or with country code).';
      return;
    }
    this.isSending = true;
    const payload = {
      messageType: this.messageType,
      messageText: this.composedMessage,
      recipientNo: this.recipientNo,
      items: this.messageType === 'INDIVIDUAL' ? this.filledItems : [],
      salaryDetails: {
        employeeName: this.employeeName.trim(),
        bankName: this.bankName.trim(),
        accountNo: this.accountNo.trim(),
        entries: this.salaryEntries.filter(e => e.date || e.description || e.amount),
      },
    };
    this.apiService.postApiCall(`${API_BASE}/send`, payload).subscribe({
      next: (res: any) => {
        this.isSending = false;
        if (res?.errorCode !== this.apiService.NO_ERROR) {
          this.formError = res?.errorDescription || 'Unable to send the message.';
          return;
        }
        if (res.Data?.deliveryStatus === 'FAILED') {
          this.formWarning = res.errorDescription || 'Message could not be sent.';
        } else {
          this.formSuccess = res.errorDescription || 'Message sent successfully.';
          this.recipientNo = '';
        }
        this.loadList(true);
      },
      error: () => {
        this.isSending = false;
        this.formError = 'Unable to send the message. Please check your connection.';
      },
    });
  }

  resetForm(): void {
    this.employeeName = '';
    this.bankName = '';
    this.accountNo = '';
    this.salaryEntries = [{ date: '', description: '', amount: '' }];
    this.recipientNo = '';
    this.clearFormMessages();
  }

  private clearFormMessages(): void {
    this.formError = '';
    this.formSuccess = '';
    this.formWarning = '';
  }

  // ── History ───────────────────────────────────────────────────────────────

  loadConfig(): void {
    this.apiService.getApiCall(`${API_BASE}/config`).subscribe({
      next: (res: any) => {
        if (res?.errorCode === this.apiService.NO_ERROR && res.Data) this.config = res.Data;
      },
    });
  }

  loadList(silent = false): void {
    if (!silent) this.isLoadingList = true;
    const criteria = JSON.stringify({ page: 1, pageSize: 100, search: this.search, deliveryStatus: this.deliveryFilter });
    this.apiService.getApiCall(`${API_BASE}/list`, criteria).subscribe({
      next: (res: any) => {
        this.isLoadingList = false;
        if (res?.errorCode === this.apiService.NO_ERROR) {
          this.rows = res.Data?.rows || [];
          this.total = res.Data?.total || 0;
          this.listError = '';
          this.lastRefreshedAt = new Date();
        } else {
          this.listError = res?.errorDescription || 'Unable to load sent messages.';
        }
      },
      error: () => {
        this.isLoadingList = false;
        this.listError = 'Unable to load sent messages. Please check your connection.';
      },
    });
  }

  toggleRow(row: SalaryConfirmation): void {
    this.expandedId = this.expandedId === row._id ? null : row._id;
  }

  resend(row: SalaryConfirmation, event: Event): void {
    event.stopPropagation();
    this.resendingId = row._id;
    this.apiService.postApiCall(`${API_BASE}/${row._id}/resend`, {}).subscribe({
      next: () => {
        this.resendingId = null;
        this.loadList(true);
      },
      error: () => {
        this.resendingId = null;
        this.listError = 'Unable to re-send the message.';
      },
    });
  }

  isItemSelected(row: SalaryConfirmation, itemId: string): boolean {
    return (row.selectedItemIds || []).includes(itemId);
  }

  shortTypeLabel(type: MessageType): string {
    return type === 'INDIVIDUAL' ? 'Individual' : this.messageTypes.find((t) => t.value === type)?.label || type;
  }

  trackById(_: number, row: SalaryConfirmation): string {
    return row._id;
  }

  deliveryBadgeClass(status: string): string {
    switch (status) {
      case 'READ': return 'badge bg-success';
      case 'DELIVERED': return 'badge bg-info';
      case 'SENT': return 'badge bg-secondary';
      case 'FAILED': return 'badge bg-danger';
      default: return 'badge bg-light text-dark';
    }
  }

  interpretationBadgeClass(value: string): string {
    switch (value) {
      case 'CONFIRMED': return 'badge bg-success';
      case 'NOT_CONFIRMED': return 'badge bg-danger';
      case 'PARTIAL': return 'badge bg-warning text-dark';
      case 'FLOW_SUBMITTED': return 'badge bg-primary';
      case 'TEXT_REPLY': return 'badge bg-info';
      default: return 'badge bg-light text-dark';
    }
  }
}
